import {
  ctorsUnion,
  CtorsUnion
} from "@genesys/client-core/lib/constructors-union";
import { MoistureHeatLoadTypeEnum } from "../../../../graphql-types";
import { PropertyValueSet } from "@genesys/property";
import { getLoadsDefMap } from "../utils";
import { SavedLoad, ActiveLoadState, LoadDef } from "./types";
import { exhaustiveCheck } from "ts-exhaustive-check";
import { Cmd } from "@typescript-tea/core";
import {
  getDefaultLoadNameAbbrevation,
  PropertyDefinitions
} from "../../../tools";
import uuid from "uuid";
import * as LoadUpdateHelper from "./load/load-update-helpers";
import * as Load from "./load";
import * as SharedState from "../../../../shared-state";
import * as SettingsOverride from "../../../components/settings-overrides";

type LoadID = string;
export interface State {
  readonly group: MoistureHeatLoadTypeEnum;
  readonly sortNo: number;
  readonly propertyDefinitions: PropertyDefinitions;
  readonly selectedLoadId: LoadID | undefined;
  readonly activeLoads: Record<LoadID, ActiveLoadState>;
}

export const init = (
  group: MoistureHeatLoadTypeEnum,
  sortNo: number,
  initialLoadSettings: ReadonlyArray<SavedLoad>,
  moistureLoadClaimValue: number,
  propertyDefinitions: PropertyDefinitions
): State => {
  const allLoads = getLoadsDefMap(moistureLoadClaimValue);

  return {
    group,
    sortNo,
    propertyDefinitions,
    selectedLoadId: undefined,
    activeLoads: initialLoadSettings.reduce(
      (acc: Record<LoadID, ActiveLoadState>, load) => {
        const initialState = Load.init(
          load,
          allLoads.get(load.loadType as MoistureHeatLoadTypeEnum)!
        );
        acc[load.id] = initialState;
        return acc;
      },
      {}
    )
  };
};

export const Action = ctorsUnion({
  addLoad: (load: LoadDef) => ({ load }),
  dispatchLoad: (
    loadId: string,
    action: Load.Action,
    loadDef: Load.LoadDef
  ) => ({ loadId, action, loadDef }),
  setSelectedLoad: (loadId: LoadID) => ({ loadId }),
  updateLoadName: (loadId: string, name: string) => ({ loadId, name })
});

export type Action = CtorsUnion<typeof Action>;

type ResultsInvalid = boolean;
export function update(
  action: Action,
  state: State,
  sharedState: SharedState.State,
  currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides,
  currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet,
  defaultSettings: PropertyValueSet.PropertyValueSet
): [
  State,
  ResultsInvalid?,
  Cmd<Action>?,
  ReadonlyArray<SharedState.Action | undefined>?
] {
  switch (action.type) {
    case "addLoad": {
      const newLoad = { ...action.load };

      const loadNameAbbreviation = getDefaultLoadNameAbbrevation(
        sharedState,
        newLoad.title
      );

      const updatedSettings = LoadUpdateHelper.updateSettingsToAddTypesToLoad(
        defaultSettings,
        "both",
        newLoad
      );

      // const currentHighestSortNo = Object.values(state.activeLoads).reduce(
      //   (acc, load) => (load.sortNo > acc ? load.sortNo : acc),
      //   -1
      // );
      const existingNames = Object.values(state.activeLoads).map(l => l.name);
      let i = 1;
      let loadName: string = "";
      do {
        loadName = `${loadNameAbbreviation}-${i}`;
        i++;
      } while (existingNames.includes(loadName));

      const activeLoadsLength = Object.values(state.activeLoads).length;
      const newActiveLoadState = Load.init(
        {
          id: uuid(),
          sortNo: activeLoadsLength,
          name: `${loadName}`,
          settings: updatedSettings
        },
        newLoad
      );

      return [
        {
          ...state,
          selectedLoadId: newActiveLoadState.id,
          activeLoads: {
            ...state.activeLoads,
            [newActiveLoadState.id]: newActiveLoadState
          }
        },
        true
      ];
    }

    case "dispatchLoad": {
      const { loadId, action: loadAction, loadDef } = action;
      const activeLoadState = state.activeLoads[loadId];
      const currentActiveLoads = { ...state.activeLoads };
      const [
        newActiveLoadState,
        resultIsInvalid,
        loadCmd,
        loadSharedStateActions
      ] = Load.update(
        loadAction,
        activeLoadState,
        sharedState,
        currentTopLevelCaseOverrides,
        currentTopLevelMoistureLoadSettings,
        state.propertyDefinitions
      );

      const notActive =
        !loadDef.additionalInfo.isHeatAdded(newActiveLoadState.settings) &&
        !loadDef.additionalInfo.isMoistureAdded(newActiveLoadState.settings);

      if (notActive) {
        delete currentActiveLoads[loadId];

        const newSelectedLoadId: string | undefined = Object.keys(
          currentActiveLoads
        ).reduce((acc, key) => {
          if (acc === undefined) {
            return key;
          }
          if (currentActiveLoads[key].sortNo > currentActiveLoads[acc].sortNo) {
            return key;
          }
          return acc;
        }, undefined);

        return [
          {
            ...state,
            selectedLoadId: newSelectedLoadId,
            activeLoads: currentActiveLoads
          },
          resultIsInvalid,
          Cmd.map(
            subaction => Action.dispatchLoad(loadId, subaction, loadDef),
            loadCmd
          ),
          loadSharedStateActions
        ];
      }

      return [
        {
          ...state,
          activeLoads: {
            ...state.activeLoads,
            [loadId]: newActiveLoadState
          }
        },
        resultIsInvalid,
        Cmd.map(
          subaction => Action.dispatchLoad(loadId, subaction, loadDef),
          loadCmd
        ),
        loadSharedStateActions
      ];
    }

    case "setSelectedLoad": {
      return [
        {
          ...state,
          selectedLoadId: action.loadId
        }
      ];
    }

    case "updateLoadName": {
      const { loadId, name } = action;
      const activeLoadState = state.activeLoads[loadId];
      const newActiveLoadState = Load.updateLoadName(
        activeLoadState,
        name,
        sharedState,
        currentTopLevelCaseOverrides,
        currentTopLevelMoistureLoadSettings,
        state.propertyDefinitions
      );
      return [
        {
          ...state,
          activeLoads: {
            ...state.activeLoads,
            [loadId]: newActiveLoadState
          }
        },
        true
      ];
    }
    default:
      return exhaustiveCheck(action, true);
  }
}

export function getAllActiveLoads(
  state: State
): ReadonlyArray<ActiveLoadState> {
  return Object.values(state.activeLoads);
}

export function addLoadToGroup(
  load: LoadDef,
  state: State,
  sharedState: SharedState.State,
  currentTopLevelCaseOverrides: SettingsOverride.SettingsOverrides,
  currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet,
  defaultSettings: PropertyValueSet.PropertyValueSet
): State {
  const [newState] = update(
    Action.addLoad(load),
    state,
    sharedState,
    currentTopLevelCaseOverrides,
    currentTopLevelMoistureLoadSettings,
    defaultSettings
  );

  return newState;
}

export function updateAllLoads(
  state: State,
  prevSettings: PropertyValueSet.PropertyValueSet,
  newSettings: PropertyValueSet.PropertyValueSet,
  loadDef: LoadDef
): State {
  const updatedActiveLoads = Object.values(state.activeLoads).reduce(
    (acc, load) => {
      const updatedLoadState = Load.overrideLoadSettings(
        load,
        prevSettings,
        newSettings,
        loadDef
      );
      acc[load.id] = updatedLoadState;
      return acc;
    },
    {} as Record<LoadID, ActiveLoadState>
  );

  return {
    ...state,
    activeLoads: updatedActiveLoads
  };
}
